import React, { useState, useEffect } from 'react';

const HorizontalGallery = ({ images }) => {
  // Default images if none are provided
  const defaultImages = [
    {
      id: 1,
      src: "/api/placeholder/220/360",
      alt: "Image 1",
      title: "Sunset Beach",
      rating: "4.8",
      likes: 15
    },
    {
      id: 2,
      src: "/api/placeholder/220/360",
      alt: "Image 2",
      title: "Mountain View",
      rating: "4.5",
      likes: 8
    },
    {
      id: 3,
      src: "/api/placeholder/220/360",
      alt: "Image 3",
      title: "City Skyline",
      rating: "4.7",
      likes: 24
    },
    {
      id: 4,
      src: "/api/placeholder/220/360",
      alt: "Image 4",
      title: "Forest Trail",
      rating: "4.6",
      likes: 6
    },
    {
      id: 5,
      src: "/api/placeholder/220/360",
      alt: "Image 5",
      title: "Desert Oasis",
      rating: "4.9",
      likes: 19
    }
  ];
  
  // Use provided images or fall back to defaults
  const initialImages = images || defaultImages;
  
  // State to track likes and active hearts
  const [galleryImages, setGalleryImages] = useState(initialImages.map(img => ({
    ...img,
    isLiked: false,
    likes: img.likes || 0,
    animatePulse: false
  })));

  // Only fetch like counts on initial mount
  useEffect(() => {
    // Fetch initial like counts for all images
    const fetchInitialLikeCounts = async () => {
      try {
        const promises = galleryImages.map(image => 
          fetch(`https://api.dramaqwizz.com/api/likes/${image.id}`)
            .then(response => response.json())
            .then(data => ({
              id: image.id,
              likes: data.success ? data.likeCount : image.likes
            }))
            .catch(error => {
              console.error(`Error fetching like count for image ${image.id}:`, error);
              return { id: image.id, likes: image.likes }; // Return original on error
            })
        );
        
        const results = await Promise.all(promises);
        
        setGalleryImages(prevImages => 
          prevImages.map(image => {
            const result = results.find(r => r.id === image.id);
            return result ? { ...image, likes: result.likes } : image;
          })
        );
      } catch (error) {
        console.error("Error fetching initial like counts:", error);
      }
    };
    
    fetchInitialLikeCounts();
  }, []); // Empty dependency array - only run once on mount

  const handleLike = async (id) => {
    // Get current image
    const currentImage = galleryImages.find(img => img.id === id);
    const willBeLiked = !currentImage.isLiked;
    const action = willBeLiked ? 'like' : 'unlike';
    
    // Update UI immediately for better UX
    setGalleryImages(prevImages => 
      prevImages.map(image => {
        if (image.id === id) {
          return {
            ...image,
            isLiked: willBeLiked,
            likes: willBeLiked ? image.likes + 1 : image.likes - 1,
            animatePulse: true
          };
        }
        return image;
      })
    );
    
    try {
      // Send like data to backend API
      const response = await fetch('https://api.dramaqwizz.com/api/likes/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          imageId: id,
          action: action
        }),
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      console.log('Success:', data);
      
      // Could update with server's like count if returned
      // For now we'll keep our local count
    } catch (error) {
      console.error('Error:', error);
      
      // Revert UI changes on error
      setGalleryImages(prevImages => 
        prevImages.map(image => {
          if (image.id === id) {
            return {
              ...image,
              isLiked: currentImage.isLiked,
              likes: currentImage.likes,
              animatePulse: false
            };
          }
          return image;
        })
      );
    } finally {
      // Reset animation after a delay regardless of success/failure
      setTimeout(() => {
        setGalleryImages(prevImages => 
          prevImages.map(image => {
            if (image.id === id) {
              return { ...image, animatePulse: false };
            }
            return image;
          })
        );
      }, 500);
    }
  };

  return (
    <div className="gallery-container" style={{ 
      width: "100%",
      overflow: "auto",
      marginTop: "20px"
    }}>
      <div className="gallery-scroll" style={{ 
        display: "flex",
        gap: "15px",
        padding: "20px",
        minWidth: "500px"
      }}>
        {galleryImages.map((image) => (
          <div 
            key={image.id} 
            className="gallery-item" 
            style={{
              minWidth: "220px",
              maxWidth: "220px",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{ height: "360px", overflow: "hidden", position: "relative" }}>
              <img 
                src={image.src} 
                alt={image.alt} 
                style={{ 
                  width: "100%", 
                  height: "100%", 
                  objectFit: "cover" 
                }} 
              />
              <button
                onClick={() => handleLike(image.id)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "rgba(255, 255, 255, 0.7)",
                  border: "none",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "transform 0.3s",
                  transform: image.animatePulse ? "scale(1.2)" : "scale(1)",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  width="20" 
                  height="20" 
                  fill={image.isLiked ? "#ff3366" : "#ccc"}
                  style={{ transition: "fill 0.3s" }}
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </button>
            </div>
            <div 
              className="description" 
              style={{
                backgroundColor: "#5D3954",
                color: "white",
                padding: "10px",
                minHeight: "100px"
              }}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px"
              }}>
                <h3 style={{ 
                  margin: "0", 
                  fontSize: "0.80rem", 
                  fontWeight: "bold",
                }}>
                  {image.title}
                </h3>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    width="14" 
                    height="14" 
                    fill="#ff3366"
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  <span style={{ fontSize: "0.75rem" }}>{image.likes}</span>
                </div>
              </div>
              <p style={{ 
                margin: "0", 
                fontSize: "0.95rem",
                lineHeight: "1.4",
                display: "flex",
                alignItems: "center",
                gap: "4px"
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="10" fill="gold">
                  <path d="M12 2l2.4 7.4h7.6l-6.2 4.5 2.4 7.4-6.2-4.5-6.2 4.5 2.4-7.4-6.2-4.5h7.6z" />
                </svg> 
                {image.rating}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HorizontalGallery;